import { Component } from '@theme/component';
import { mediaQueryLarge, ResizeNotifier, requestIdleCallback } from '@theme/utilities';

/**
 * @typedef {Object} GroupSliderDragState
 * @property {number} pointerId
 * @property {number} startX
 * @property {number} startScrollLeft
 * @property {boolean} moved
 */

/**
 * @typedef {Object} GroupSliderScrollbarDragState
 * @property {number} pointerId
 * @property {HTMLElement} track
 */

class GroupSlider extends Component {
  #resizeObserver = new ResizeNotifier(() => this.#sync());
  /** @type {GroupSliderDragState | null} */
  #drag = null;
  /** @type {GroupSliderScrollbarDragState | null} */
  #scrollbarDrag = null;
  #suppressClick = false;
  #selectionLockCount = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this);
    mediaQueryLarge.addEventListener('change', this.#sync);
    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointermove', this.#handlePointerMove);
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#handlePointerUp);
    this.addEventListener('click', this.#handleClickCapture, true);
    this.addEventListener('dragstart', this.#handleDragStart);
    this.addEventListener('scroll', this.#updateScrollbar, { passive: true });
    // Fallback: before the drag threshold there is no pointer capture, so a pointer
    // released outside the slider would otherwise leave the press state stuck.
    document.addEventListener('pointerup', this.#handlePointerUp);
    document.addEventListener('pointercancel', this.#handlePointerUp);
    this.parentElement?.addEventListener('pointerdown', this.#handleScrollbarPointerDown);
    document.addEventListener('pointermove', this.#handleScrollbarPointerMove);
    document.addEventListener('pointerup', this.#handleScrollbarPointerUp);
    document.addEventListener('pointercancel', this.#handleScrollbarPointerUp);
    requestIdleCallback(() => this.#sync());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    mediaQueryLarge.removeEventListener('change', this.#sync);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('pointerup', this.#handlePointerUp);
    this.removeEventListener('pointercancel', this.#handlePointerUp);
    this.removeEventListener('click', this.#handleClickCapture, true);
    this.removeEventListener('dragstart', this.#handleDragStart);
    this.removeEventListener('scroll', this.#updateScrollbar);
    document.removeEventListener('pointerup', this.#handlePointerUp);
    document.removeEventListener('pointercancel', this.#handlePointerUp);
    this.parentElement?.removeEventListener('pointerdown', this.#handleScrollbarPointerDown);
    document.removeEventListener('pointermove', this.#handleScrollbarPointerMove);
    document.removeEventListener('pointerup', this.#handleScrollbarPointerUp);
    document.removeEventListener('pointercancel', this.#handleScrollbarPointerUp);
    this.#unlockSelection();
  }

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    if (!mediaQueryLarge.matches || !this.hasAttribute('data-group-slider-active')) return;
    if (event.button !== 0) return;
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('button, input, select, textarea, summary, label')) return;

    this.#drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: this.scrollLeft,
      moved: false,
    };

    this.setAttribute('data-group-slider-pressing', '');
    this.#lockSelection();
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (!this.#drag || event.pointerId !== this.#drag.pointerId) return;

    const deltaX = event.clientX - this.#drag.startX;

    if (!this.#drag.moved && Math.abs(deltaX) > 4) {
      this.#drag.moved = true;
      this.setAttribute('data-group-slider-dragging', '');
      // Capture only once it is a real drag. Capturing on pointerdown retargets the
      // compatibility click to this element, which breaks links inside the slider.
      this.setPointerCapture(event.pointerId);
    }

    if (!this.#drag.moved) return;

    this.scrollLeft = this.#drag.startScrollLeft - deltaX;
    event.preventDefault();
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    if (!this.#drag || event.pointerId !== this.#drag.pointerId) return;

    if (this.hasPointerCapture(event.pointerId)) {
      this.releasePointerCapture(event.pointerId);
    }

    this.removeAttribute('data-group-slider-pressing');
    this.removeAttribute('data-group-slider-dragging');
    this.#unlockSelection();

    if (this.#drag.moved) {
      this.#suppressClick = true;
      setTimeout(() => {
        this.#suppressClick = false;
      }, 120);
    }

    this.#drag = null;
  };

  /** @param {MouseEvent} event */
  #handleClickCapture = (event) => {
    if (!this.#suppressClick) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  };

  /** @param {DragEvent} event */
  #handleDragStart = (event) => {
    if (!this.hasAttribute('data-group-slider-active')) return;

    event.preventDefault();
  };

  /** @param {PointerEvent} event */
  #handleScrollbarPointerDown = (event) => {
    if (!this.hasAttribute('data-group-slider-active')) return;
    if (event.button !== 0) return;
    if (!(event.target instanceof HTMLElement)) return;

    const scrollbar = event.target.closest('.group-slider-scrollbar');
    if (!(scrollbar instanceof HTMLElement) || scrollbar.previousElementSibling !== this) return;

    const track = scrollbar.querySelector('.group-slider-scrollbar__track');
    if (!(track instanceof HTMLElement)) return;

    this.#scrollbarDrag = {
      pointerId: event.pointerId,
      track,
    };

    scrollbar.setPointerCapture(event.pointerId);
    this.setAttribute('data-group-slider-scrollbar-dragging', '');
    this.#lockSelection();
    this.#scrollToScrollbarPosition(event.clientX, track);
    event.preventDefault();
  };

  /** @param {PointerEvent} event */
  #handleScrollbarPointerMove = (event) => {
    if (!this.#scrollbarDrag || event.pointerId !== this.#scrollbarDrag.pointerId) return;

    this.#scrollToScrollbarPosition(event.clientX, this.#scrollbarDrag.track);
    event.preventDefault();
  };

  /** @param {PointerEvent} event */
  #handleScrollbarPointerUp = (event) => {
    if (!this.#scrollbarDrag || event.pointerId !== this.#scrollbarDrag.pointerId) return;

    const scrollbar = this.#getScrollbar();
    if (scrollbar?.hasPointerCapture(event.pointerId)) {
      scrollbar.releasePointerCapture(event.pointerId);
    }

    this.removeAttribute('data-group-slider-scrollbar-dragging');
    this.#scrollbarDrag = null;
    this.#unlockSelection();
  };

  /**
   * @param {number} clientX
   * @param {HTMLElement} track
   */
  #scrollToScrollbarPosition(clientX, track) {
    const rect = track.getBoundingClientRect();
    const progress = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
    const clampedProgress = Math.max(0, Math.min(progress, 1));

    this.scrollLeft = (this.scrollWidth - this.clientWidth) * clampedProgress;
  }

  #lockSelection() {
    this.#selectionLockCount += 1;
    document.documentElement.style.setProperty('user-select', 'none');
    document.documentElement.style.setProperty('-webkit-user-select', 'none');
  }

  #unlockSelection() {
    if (this.#selectionLockCount > 0) {
      this.#selectionLockCount -= 1;
    }

    if (this.#selectionLockCount > 0) return;

    document.documentElement.style.removeProperty('user-select');
    document.documentElement.style.removeProperty('-webkit-user-select');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getScrollbar() {
    const scrollbar = this.nextElementSibling;
    return scrollbar instanceof HTMLElement && scrollbar.classList.contains('group-slider-scrollbar') ? scrollbar : null;
  }

  #updateScrollbar = () => {
    const scrollbar = this.#getScrollbar();
    if (!scrollbar) return;

    const track = scrollbar.querySelector('.group-slider-scrollbar__track');
    if (!(track instanceof HTMLElement)) return;

    const scrollableWidth = this.scrollWidth - this.clientWidth;
    const trackWidth = track.clientWidth;
    const thumbWidth = scrollableWidth > 0 ? Math.max((this.clientWidth / this.scrollWidth) * trackWidth, 40) : trackWidth;
    const maxOffset = Math.max(trackWidth - thumbWidth, 0);
    const thumbOffset = scrollableWidth > 0 ? (this.scrollLeft / scrollableWidth) * maxOffset : 0;

    scrollbar.style.setProperty('--group-slider-scrollbar-thumb-width', `${thumbWidth}px`);
    scrollbar.style.setProperty('--group-slider-scrollbar-thumb-offset', `${thumbOffset}px`);
  };

  #sync = () => {
    const isDesktop = mediaQueryLarge.matches;
    const enabled = isDesktop ? this.dataset.sliderDesktop === 'true' : this.dataset.sliderMobile === 'true';
    const children = Array.from(this.children).filter((child) => child instanceof HTMLElement);

    this.removeAttribute('data-group-slider-active');
    this.removeAttribute('data-group-slider-pressing');
    this.removeAttribute('data-group-slider-dragging');
    this.removeAttribute('data-group-slider-scrollbar-dragging');
    this.#updateScrollbar();

    for (const child of children) {
      child.style.removeProperty('--group-slider-item-width');
    }

    if (!enabled || children.length < 2 || !this.classList.contains('layout-panel-flex--row')) return;
    if (!isDesktop && this.classList.contains('mobile-column')) return;

    const computedStyle = getComputedStyle(this);
    const gap = parseFloat(computedStyle.columnGap || computedStyle.gap || '0');
    const previousFlexWrap = this.style.getPropertyValue('flex-wrap');
    const previousOverflowX = this.style.getPropertyValue('overflow-x');
    const previousOverflowY = this.style.getPropertyValue('overflow-y');
    const previousWidth = this.style.getPropertyValue('width');
    const previousFlexShrink = children.map((child) => child.style.getPropertyValue('flex-shrink'));

    this.style.setProperty('width', '100%');
    this.style.setProperty('flex-wrap', 'nowrap');
    this.style.setProperty('overflow-x', 'hidden');
    this.style.setProperty('overflow-y', 'hidden');

    for (const child of children) {
      child.style.setProperty('flex-shrink', '0');
    }

    const widths = children.map((child) => child.getBoundingClientRect().width);
    const totalWidth = widths.reduce((sum, width) => sum + width, 0) + gap * Math.max(children.length - 1, 0);
    const containerWidth = this.clientWidth;

    if (previousFlexWrap) {
      this.style.setProperty('flex-wrap', previousFlexWrap);
    } else {
      this.style.removeProperty('flex-wrap');
    }

    if (previousOverflowX) {
      this.style.setProperty('overflow-x', previousOverflowX);
    } else {
      this.style.removeProperty('overflow-x');
    }

    if (previousOverflowY) {
      this.style.setProperty('overflow-y', previousOverflowY);
    } else {
      this.style.removeProperty('overflow-y');
    }

    if (previousWidth) {
      this.style.setProperty('width', previousWidth);
    } else {
      this.style.removeProperty('width');
    }

    children.forEach((child, index) => {
      if (previousFlexShrink[index]) {
        child.style.setProperty('flex-shrink', previousFlexShrink[index]);
      } else {
        child.style.removeProperty('flex-shrink');
      }
    });

    if (totalWidth <= containerWidth + 1) return;

    for (const [index, child] of children.entries()) {
      child.style.setProperty('--group-slider-item-width', `${widths[index]}px`);
    }

    this.setAttribute('data-group-slider-active', '');
    requestAnimationFrame(this.#updateScrollbar);
  };
}

if (!customElements.get('group-slider-component')) {
  customElements.define('group-slider-component', GroupSlider);
}
